import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { Button, Stack, Typography, Box, InputAdornment, useMediaQuery, useTheme, Grid, Avatar, CircularProgress } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Person from '@mui/icons-material/Person';
import { useCapsLockDetector } from '../hooks/useCapsLockDetector';
import { useAddFcmTokenMutation, useLoginMutation } from '../authApiSlice';
import { selectCurrentUser, selectDeviceType, selectDeviceVersion, selectFcmToken, setCredentials as setCredential } from '../authSlice';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import CustomTextField from '../../../common/Form/TextField';
import sendNotification from '../../../utils/sendNotification';

const validationSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

const LoginForm = ({ onFocus, onBlur, onSwitch }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [login] = useLoginMutation();
  const [addFcmToken] = useAddFcmTokenMutation();
  const isCapsLockOn = useCapsLockDetector();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fcmToken = useSelector(selectFcmToken);
  const deviceType = useSelector(selectDeviceType);
  const deviceVersion  = useSelector(selectDeviceVersion);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsLoading(true);
        const data = {
          ...values,
          navigator: {
            userAgent: window.navigator.userAgent,
            language: window.navigator.language,
            platform: window.navigator.platform,
            vendor: window.navigator.vendor,
          },
          fcmToken: fcmToken,
        };
        const response = await login(data).unwrap();
        dispatch(setCredential({ ...response }));
        window['ReactNativeWebView']?.postMessage(
          JSON.stringify({ type: 'localStorage', payload: { keys: 'user', values: { ...response } } })
        );
        const addFcmTokenResponse = await addFcmToken({
          empId: response?.user?.empId,
          deviceToken: fcmToken,
          deviceType: deviceType,
        }).unwrap();
        if (rememberMe) {
          localStorage.setItem('username', values.username);
        } else {
          localStorage.removeItem('username');
        }
        setIsLoading(false);
        // const logoPath = await resolveResource('icons/linkway.png');
        // console.log(logoPath, 'LOGO');
        // sendNotification({
        //   title: 'Linkway',
        //   body: `${response?.user?.userName} logged in successfully!!`,
        //   icon: 'src-tauri/icons/icon.ico',
        //   sound: 'default',
        // });
        if (values.username == 'vendor' && values.password == 'vendor') {
          navigate('/vendor');
        }
        if (values.username == 'canteen' && values.password == 'canteen') {
          navigate('/canteen');
        }
        navigate('/');
      } catch (error) {
        resetForm();
        if (error.data) {
          setIsLoading(false);
          toast.error(error.data.message || error.data.error || 'An error occurred', { position: 'top-center' });
        } else {
          setIsLoading(false);
          toast.error('An error occurred while logging in', { position: 'top-center' });
        }
      }
    },
  });

  const fields = [
    {
      name: 'username',
      label: 'Username *',
      type: 'text',
      endAdornment: <Person sx={{ color: 'text.primary' }} />,
    },
    {
      name: 'password',
      label: 'Password *',
      type: showPassword ? 'text' : 'password',
      endAdornment: showPassword ? (
        <VisibilityOff
          sx={{ color: 'text.primary', cursor: 'default' }}
          onClick={() => setShowPassword((prev) => !prev)}
          edge="end"
        />
      ) : (
        <Visibility
          sx={{ color: 'text.primary', cursor: 'default' }}
          onClick={() => setShowPassword((prev) => !prev)}
          edge="end"
        />
      ),
    },
  ];
  


 


  useEffect(() => {
    const savedusername = localStorage.getItem('username');
    if (savedusername) {
      formik.setFieldValue('username', savedusername);
      setRememberMe(true);
    }
  }, []);

  return (
    <>
      {isSmallScreen ? null : (
        <Grid width="100%">
          <Typography sx={{ fontWeight: 'bold' }} align="left" variant={isSmallScreen ? 'h6' : 'h4'}>
            Welcome Amigos!
          </Typography>
          <Typography
            variant="body2"
            align="left"
            sx={{ color: 'grey' }}
            paragraph
            mt={isSmallScreen ? '0rem' : '1rem'}
          >
            Been here, done that? Just whisper <strong>'Alohomora'</strong> with your credentials or take the shortcut
            with the button below.
          </Typography>
        </Grid>
      )}
      <Box textAlign="center" sx={{ display: 'flex', flexDirection: 'column' }}>
        <form onSubmit={formik.handleSubmit}>
          {isSmallScreen ? null : (
            <Typography variant="body2" align="left" sx={{ color: 'grey' }} paragraph>
              No need to SignUp! Use just ERP credentials.
            </Typography>
          )}
          {fields.map((field) => (
            <CustomTextField
              key={field.name}
              margin="normal"
              label={field.label}
              name={field.name}
              type={field.type}
              variant="outlined"
              value={formik.values[field.name]}
              onChange={formik.handleChange}
              onBlur={(e) => {
                formik.handleBlur(e);
                if (onBlur) onBlur(e);
              }}
              onFocus={onFocus}
              error={formik.touched[field.name] && !!formik.errors[field.name]}
              helperText={formik.touched[field.name] && formik.errors[field.name]}
              InputProps={{ endAdornment: <InputAdornment position="end">{field.endAdornment}</InputAdornment> }}
              sx={{
                width: '100%',
                '& .MuiInputLabel-root': {
                  fontSize: isSmallScreen ? '0.8rem' : '0.9rem',
                },
              }}
            />
          ))}
          {isCapsLockOn && (
            <Typography variant="body2" color="gray">
              Caps Lock is on
            </Typography>
          )}
          <Button
            fullWidth
            type="submit"
            variant="contained"
            sx={{
              marginTop: { xs: '1.5rem', sm: '2.5rem', md: '1.5rem', xl: '1.5rem' },
              borderRadius: '10px',
              background: 'linear-gradient(90deg, #0A4FAC, #498dcb)',
              color: '#fff', // Ensure the text color is visible against the gradient
              '&:hover': {
                background: 'linear-gradient(90deg, #0A4FAC, #498dcb)',
                filter: 'brightness(1.1)', // Slightly brighten on hover
              },
            }}
          >
           { isLoading ? <CircularProgress size={24} color='secondary'/> : <b>Sign in</b> }
           {/* { isLoading ? <b>Loading</b> : <b>Sign in</b> } */}
          </Button>
        </form>
        {isSmallScreen ? null : (
          <Grid sx={{ mt: { xs: 4, sm: 2, md: 2, lg: 7, xl: 10 } }} textAlign="center" width="100%">
            <Typography variant="body2" color="grey">
              © 2024 Gateway Group. All rights reserved.
            </Typography>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default LoginForm;
